<script>
import CartOrderDetails from '@/components/molecules/CartOrderDetails'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import ImageWrapper from '@/components/atoms/ImageWrapper'
import rate from '@/modules/mixins/rate'
import Rate from '@/components/molecules/Rate'
import event from '@/modules/mixins/event'
import OrderDetails from '@/components/molecules/OrderDetails'
import ModalConfirm from '@/components/organism/modal/ModalConfirm'
import Modal from '@/components/atoms/Modal.vue'

export default {
    name: 'OrderBlock',
    components: {
        Modal,
        ModalConfirm,
        OrderDetails,
        Rate,
        ImageWrapper,
        ButtonLoader,
        CartOrderDetails,
    },
    mixins: [rate, event],
    props: {
        model: null,
        pusher: null,
        now: null,
        pending: Boolean,
        trackingList: Array,
    },
    data() {
        return {
            reorderLoading: false,
            cancelConfirmOpen: false,
            cancelLoading: false,
            showOrder: false,
            trackOrder: null,
            order: null,
            cancelError: false,
        }
    },
    computed: {
        locale() {
            return this.$store.getters[`locale`]
        },
        restaurant() {
            return this.order?.restaurant || {}
        },
        smDevice() {
            return this.$store.getters[`smDevice`]
        },
        requiredDeliveryTime() {
            return this.order?.required_delivery_time
        },
        isToday() {
            return this.$moment().isSame(this.requiredDeliveryTime, 'day')
        },
        isTomorrow() {
            return this.$moment().add(1, 'days').isSame(this.requiredDeliveryTime, 'day')
        },
        isThisYear() {
            return this.$moment().isSame(this.requiredDeliveryTime, 'year')
        },
        restaurantManaged() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/restaurantManaged`]
                : undefined
        },
        orderNew() {
            return this.namespace ? this.$store.getters[`${this.namespace}/orderNew`] : undefined
        },
        orderApproved() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/orderApproved`]
                : undefined
        },
        deliveryStatus() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/deliveryStatus`]
                : undefined
        },
        deliveryCancelled() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/deliveryCancelled`]
                : undefined
        },
        deliveryInProgress() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/deliveryInProgress`]
                : undefined
        },
        deliveryFinished() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/deliveryFinished`]
                : undefined
        },
        deliveryTime() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/deliveryTime`]
                : undefined
        },
        orderProcessing() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/orderProcessing`]
                : undefined
        },
        statusKey() {
            return this.namespace ? this.$store.getters[`${this.namespace}/statusKey`] : undefined
        },
        courierStatusText() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/courierStatusText`]
                : undefined
        },
        cancelableUntil() {
            return this.order?.cancelable_until
        },
        cancelableUntilToday() {
            return this.$moment().isSame(this.cancelableUntil, 'day')
        },
        cancelableUntilTomorrow() {
            return this.$moment().add(1, 'days').isSame(this.cancelableUntil, 'day')
        },
        orderCancelled() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/orderCancelled`]
                : undefined
        },
        orderCancelledAfterApprove() {
            return this.namespace
                ? this.$store.getters[`${this.namespace}/orderCancelledAfterApprove`]
                : undefined
        },
        showCancelButton() {
            if (this.orderApproved) {
                return false
            }
            return (
                (this.now.isBefore(this.cancelableUntil) &&
                    !this.orderCancelled &&
                    !this.orderCancelledAfterApprove) ||
                this.cancelConfirmOpen
            )
        },
        isCanteenInProgress() {
            return this.order.cloudcanteen && !this.order.master_canteen_order
        },
    },
    async created() {
        this.order = this.model
        this.namespace = this.order.identifier
        this.trackOrder = this.trackingList?.find((order) => order.id === this.order.id)
        this.$store.registerDelivery(this.namespace)
        await this.$store.dispatch(`${this.namespace}/setOrder`, this.order.model)
        await this.subscribeOrderChannel(this.order.id)
        if (this.trackOrder) {
            await this.$store.dispatch(`${this.namespace}/setTracking`, this.trackOrder)
            this.subscribeTrackerChannel(this.trackOrder.id)
        }
    },
    beforeDestroy() {
        this.unsubscribeOrderChannel()
        if (this.trackOrder) this.unsubscribeTrackerChannel()
    },
    methods: {
        viewOrder() {
            this.$refs.orderDetails.open()
        },
        reorder() {
            if (!this.$user.onlyCloudCanteen) {
                this.reorderLoading = true
                this.$cart.buildCart(this.order.model).finally(() => {
                    this.reorderLoading = false
                })
            }
        },

        reloadOrders() {
            this.$emit('canceled')
        },

        async cancelOrder() {
            try {
                await this.order.cancelOrder()
                this.$emit('canceled')
            } catch (e) {
                this.$refs.confirmModal.close()
                this.cancelError = true
            }
        },
        formatTime(date, todayOrTomorrow = false) {
            let format = todayOrTomorrow
                ? this.$hour
                : this.locale === 'hu'
                ? this.$dayMonthTimeFormat_HU
                : this.$dayMonthTimeFormat_EN
            return this.$moment(date).locale(this.locale).format(format)
        },
    },
}
</script>

<template lang="pug">
.card.order-block(v-if="order" :class="{'border-0': !smDevice}")
    .card-body
        .row.mx-n2
            .col-4.col-lg-3.px-2
                image-wrapper.border-radius-xs(:src="order.restaurant.card_image" height="100px" fill)
            .col.d-flex.px-2.flex-column.align-items-start
                label {{order.restaurant.name}} - {{order.identifier}}
                    span.text-sm.text-danger(v-if="orderCancelled || orderCancelledAfterApprove")
                        template(v-if="statusKey") &nbsp;{{$t(`enums.${statusKey}.title`)}}
                        template(v-else) &nbsp;{{courierStatusText}}
                span.text-sm(:key="`deliveryStatus-${deliveryStatus}-${_uid}`")
                    template(v-if="!(orderCancelled || orderCancelledAfterApprove)")
                        span.text-sm
                            template(v-if="deliveryFinished") {{$t('base.delivered')}}
                            template(v-else)
                                template(v-if="orderProcessing")
                                    | {{$t('enums.processing')}}
                                template(v-else-if="statusKey")
                                    | {{$t(`enums.${statusKey}.title`)}}
                                template(v-else)
                                    | {{courierStatusText}}
                        template &nbsp;•&nbsp;
                    template(v-if="!isThisYear")  {{ requiredDeliveryTime | toDate }}
                    template(v-else-if="isToday") {{ $t('labels.today', {time: formatTime(requiredDeliveryTime, true)}) }}
                    template(v-else-if="isTomorrow") {{ $t('labels.tomorrow', {time: formatTime(requiredDeliveryTime, true)}) }}
                    template(v-else) {{formatTime(requiredDeliveryTime)}}
                button.btn.btn-link(v-if="deliveryFinished || restaurantManaged || orderCancelled || deliveryCancelled" @click="viewOrder") {{$t('labels.view_order')}}
                span.text-sm
                    template {{order.price_sum | toCurrency(order.currency_locale, order.currency)}}
                    template &nbsp;•&nbsp;{{$t('base.items', {count: order.items.length}, order.items.length)}}
                rate(v-if="deliveryFinished" :value="order.restaurant_rating ? order.restaurant_rating.rating : null" @input="rateOrder")
            .col-12.col-lg-4.px-2.mt-4.mt-lg-0(v-if="deliveryInProgress || order.can_order_again || isCanteenInProgress")
                router-link.btn.btn-primary.btn-block(v-if="deliveryInProgress || isCanteenInProgress" :to="{name:'summary-page', params:{id: order.id}}")  {{$t('labels.view_order')}}
                button-loader.btn.btn-secondary.btn-block(v-else-if="order.can_order_again" :loading="reorderLoading" @click="reorder") {{$t('labels.order_again')}}
                .d-flex.flex-column.align-items-center.mt-4(v-if="showCancelButton")
                    modal-confirm(
                        ref="confirmModal"
                        v-model="cancelConfirmOpen"
                        :action="cancelOrder"
                        :header-label="$t('order.cancel_order')"
                        :cancel-label="$t('base.cancel')"
                        :confirm-label="$t('base.confirm')"
                        :disabled="orderCancelled || orderCancelledAfterApprove"
                        :button-class="orderCancelled || orderCancelledAfterApprove ? 'btn btn-link text-decoration-line-through text-73' : 'btn btn-link text-danger'"
                    ) {{$t('base.cancel_order')}}
                    modal(v-model="cancelError" @close="reloadOrders" @closed="reloadOrders")
                      template(v-slot:body)
                          .d-flex.align-items-center.flex-column
                              icon.mb-6(style="font-size:50px" icon="fa-solid fa-exclamation-triangle")
                              .modal-header-text.mb-6 {{ $t('order.cancel_order_error') }}
                    small.ml-2.text-73(:key="locale")
                        template {{$t('order.cancelable_until')}}&nbsp;
                        template(v-if="cancelableUntilToday") {{ $t('labels.today', {time: formatTime(cancelableUntil, true)}) }}
                        template(v-else-if="cancelableUntilTomorrow")  {{ $t('labels.tomorrow', {time: formatTime(cancelableUntil, true)}) }}
                        template(v-else) {{formatTime(cancelableUntil)}}
    order-details(
        ref="orderDetails"
        :order="order"
        :restaurant="restaurant"
        :order-processing="orderProcessing"
        :status-key="statusKey"
        :delivery-finished="deliveryFinished"
        :courier-status-text="courierStatusText")
</template>
